import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2 } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormGroup,
  InputLabel,
  ErrorText,
  ModalCancelButton,
  ModalSubmitButton,
} from '../../Navbar/modals/changePasswordModal.styles';
import { renameApplication } from '../../../services/dashboard';
import {
  // TextAreaInput,
  TextInput,
} from '../../UploadDocuments/uploadDocuments.styles';

const RenameApplicationModal = ({ isOpen, onClose, application }) => {
  const [title, setTitle] = useState('');
  // const [description, setDescription] = useState('');
  const [errors, setErrors] = useState({});
  const overlayRef = useRef(null);

  const queryClient = useQueryClient();

  // Auto-fill fields whenever a new application is passed in / modal opens
  useEffect(() => {
    if (isOpen && application) {
      setTitle(application.title || '');
      // setDescription(application.description || '');
      setErrors({});
    }
  }, [isOpen, application]);

  const mutation = useMutation({
    mutationFn: renameApplication,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['applicationList'] });
      window.alert('Application updated successfully');
      handleClose();
    },
    onError: (err) => {
      setErrors({
        form:
          err?.response?.data?.message ||
          err?.message ||
          'Failed to update application. Please try again.',
      });
    },
  });

  const handleClose = () => {
    setTitle('');
    // setDescription('');
    setErrors({});
    onClose();
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const nextErrors = {};
    if (!title.trim()) nextErrors.title = 'Title is required';
    // if (!description.trim()) nextErrors.description = 'Description is required';

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    mutation.mutate({
      applicationId: application?._id,
      title: title.trim(),
      // description: description.trim(),
    });
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') handleClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <ModalOverlay
      onClick={handleClose}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-labelledby="rename-modal-title"
      ref={overlayRef}
    >
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle id="rename-modal-title">Edit application</ModalTitle>
          <ModalCloseButton onClick={handleClose} type="button" aria-label="Close">
            <X size={16} />
          </ModalCloseButton>
        </ModalHeader>

        <form onSubmit={handleSubmit}>
          <ModalBody>
            <FormGroup>
              <InputLabel htmlFor="app-title">Title</InputLabel>
              <TextInput
                id="app-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter title"
                $hasError={!!errors.title}
                aria-describedby={errors.title ? 'app-title-error' : undefined}
              />
              {errors.title && (
                <ErrorText id="app-title-error" role="alert">
                  {errors.title}
                </ErrorText>
              )}
            </FormGroup>

            {/* <FormGroup>
              <InputLabel htmlFor="app-description">Description</InputLabel>
              <TextAreaInput
                id="app-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter description"
                rows={5}
                $hasError={!!errors.description}
                aria-describedby={errors.description ? 'app-description-error' : undefined}
              />
              {errors.description && (
                <ErrorText id="app-description-error" role="alert">
                  {errors.description}
                </ErrorText>
              )}
            </FormGroup> */}

            {errors.form && <ErrorText role="alert">{errors.form}</ErrorText>}
          </ModalBody>

          <ModalFooter>
            <ModalCancelButton type="button" onClick={handleClose}>
              Cancel
            </ModalCancelButton>
            <ModalSubmitButton type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? (
                <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
              ) : null}
              {mutation.isPending ? 'Saving…' : 'Save changes'}
            </ModalSubmitButton>
          </ModalFooter>
        </form>
      </ModalContent>
    </ModalOverlay>,
    document.body
  );
};

export default RenameApplicationModal;
